/**
 * Iframe Event Data — Base64 URL Param Approach
 *
 * Partners embed Amber's whitelabel website in an iframe and can pass custom
 * key-value event data via postMessage. This module handles:
 *
 * 1. Storing data in memory when received via postMessage (in the iframe)
 * 2. Encoding data as a base64 URL param (_ied) for outgoing links
 * 3. Decoding _ied from URL on page load and persisting to localStorage
 * 4. Reading from localStorage on lead form submission
 *
 * Why this approach?
 * - The iframe runs as a third-party context (different origin than parent page)
 * - Chrome 115+ partitions localStorage/sessionStorage for third-party iframes
 * - Cookies with SameSite=None are also blocked when third-party cookies are disabled
 * - The only reliable way to pass data from iframe → new tab is via URL params
 * - Once on the property page (first-party), localStorage works normally
 *
 * Flow:
 * 1. Partner sends: postMessage({ type: 'amber_custom_event', payload: { ... } })
 * 2. IframeEventListener stores payload in memory via setIframeEventData()
 * 3. When links are built, getIframeEventDataParam() returns { _ied: "<base64>" }
 * 4. On page load, hydrateIframeEventDataFromUrl() reads _ied → stores in localStorage
 * 5. On lead submit, getIframeEventData() reads from localStorage
 * 6. After submission, clearIframeEventData() cleans up
 */

const LOCALSTORAGE_KEY = "amber_iframe_event_data";
const URL_PARAM_KEY = "_ied";

// Module-level in-memory store (used in iframe context before navigation)
let inMemoryData: Record<string, unknown> = {};

/**
 * Validate that a payload is a plain object with serializable values.
 */
const isValidPayload = (payload: unknown): payload is Record<string, unknown> => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }
  try {
    JSON.stringify(payload);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Encode data to a URL-safe base64 string.
 */
const encodeToBase64 = (data: Record<string, unknown>): string => {
  try {
    const json = JSON.stringify(data);
    // Use btoa for browser, Buffer for SSR (though this should only run client-side)
    if (typeof btoa !== "undefined") {
      return btoa(encodeURIComponent(json));
    }
    return Buffer.from(encodeURIComponent(json)).toString("base64");
  } catch (e) {
    return "";
  }
};

/**
 * Decode a base64 string back to data.
 */
const decodeFromBase64 = (encoded: string): Record<string, unknown> | null => {
  try {
    let json: string;
    if (typeof atob !== "undefined") {
      json = decodeURIComponent(atob(encoded));
    } else {
      json = decodeURIComponent(Buffer.from(encoded, "base64").toString());
    }
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Store iframe event data in memory. Multiple calls merge payloads.
 * Called by IframeEventListener when postMessage is received.
 */
export const setIframeEventData = (payload: Record<string, unknown>): void => {
  if (!isValidPayload(payload)) {
    return;
  }
  inMemoryData = { ...inMemoryData, ...payload };
};

/**
 * Get the _ied URL param to append to outgoing links.
 * Returns { _ied: "<base64>" } if data exists, or {} if no data.
 * Used alongside getIframeSourceParam() when building property/search URLs.
 */
export const getIframeEventDataParam = (): { _ied?: string } => {
  // Prefer in-memory data (we're in the iframe, data was just received via postMessage)
  if (Object.keys(inMemoryData).length > 0) {
    const encoded = encodeToBase64(inMemoryData);
    return encoded ? { [URL_PARAM_KEY]: encoded } : {};
  }

  // Fall back to localStorage (we're on a first-party page, data was already decoded)
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
          const encoded = encodeToBase64(parsed);
          return encoded ? { [URL_PARAM_KEY]: encoded } : {};
        }
      }
    } catch (e) {
      // ignore
    }
  }

  return {};
};

/**
 * On page load, read _ied from URL and store in localStorage.
 * This bridges the gap: data encoded in URL (from iframe) gets stored
 * in first-party localStorage for lead form submission.
 *
 * Should be called once on app mount (client-side only).
 */
export const hydrateIframeEventDataFromUrl = (): void => {
  if (typeof window === "undefined") return;

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const encoded = urlParams.get(URL_PARAM_KEY);
    if (!encoded) return;

    const decoded = decodeFromBase64(encoded);
    if (decoded && Object.keys(decoded).length > 0) {
      // Store in localStorage for lead form
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(decoded));
      // Also populate in-memory store (for carrying forward on subsequent links)
      inMemoryData = decoded;
    }
  } catch (e) {
    // ignore — bad data in URL param
  }
};

/**
 * Retrieve stored iframe event data for lead submission.
 * Checks in-memory first, then localStorage.
 */
export const getIframeEventData = (): Record<string, unknown> | null => {
  // Check in-memory store
  if (Object.keys(inMemoryData).length > 0) {
    return { ...inMemoryData };
  }

  // Check localStorage
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return { ...parsed };
        }
      }
    } catch (e) {
      // ignore
    }
  }

  return null;
};

/**
 * Clear all stored iframe event data. Called after successful lead submission.
 */
export const clearIframeEventData = (): void => {
  inMemoryData = {};
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(LOCALSTORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }
};

/**
 * Append _ied param to an already-constructed URL string.
 * Similar to appendIframeSourceToUrl — used for URLs built outside the params pattern.
 */
export const appendIframeEventDataToUrl = <T extends string | null | undefined>(url: T): T => {
  if (!url) return url;
  const param = getIframeEventDataParam();
  if (!param._ied) return url;
  if (/[?&]_ied=/.test(url)) return url; // already present
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_ied=${encodeURIComponent(param._ied)}` as T;
};
